import React, { useEffect } from "react";
import Helmet from "react-helmet";
import { helmetJsonLdProp } from "react-schemaorg";
import { Link } from "react-router-dom";

import { Container, Col, Row } from "react-bootstrap/esm/index";

// Import Components
import ITPHeroHeader from "../../components/HeroHeader/ITPHeroHeader";
import ItpNavigation from "../../components/ITPNavigation/ITPNavigation";
import FooterITP from "../../components/shared-ui/Footer/Footer-ITP";
import BackToTopBtn from "../../components/shared-ui/BackToTopBtn/BackToTopBtn";
import ISIITPinline from "../../components/shared-ui/ISI/ISIITP-inline";
import ISIwrapper from "../../components/shared-ui/ISI/ISI-wrapper";
import ISIITP from "../../components/shared-ui/ISI/ISIITP";
import LookingForSupport from "../../components/shared-ui/Cards/LookingForSupport";

// Import Styles
import "../../css/ITPHome.scss";
import "../../css/Cards.scss";
import "../../css/VideoSection.scss";
import NowApprovedNotification from "../../components/Notifications/NowApprovedNotification";

function ITPHome() {
  useEffect(() => {
    window.scrollTo(0, 0);
    let location = window.location.pathname;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page: {
        path: `${location}`,
        title: "Immune Thrombocytopenia (ITP) | DOPTELET® (avatrombopag)",
        environment: "dev",
        indication: "ITP",
        audience: "DTC",
      },
    });
  }, []);
  return (
    <div className="main_content_wrap">
      <Helmet
        script={[
          helmetJsonLdProp({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                url: "https://doptelet.com/home/",
                name: "Chronic Immune Thrombocytopenia (ITP) Treatment | DOPTELET® (avatrombopag)",
                description:
                  "Doptelet is an oral chronic ITP medication that helps increase platelet counts to 50,000 per microliter or more, in as few as 8 days, and can keep them lifted for an average of 12 total weeks",
                mainEntity: {
                  "@type": "Drug",
                  name: "Doptelet",
                  alternateName: "avatrombopag",
                  description:
                    "Doptelet is a thrombopoietin receptor agonist used to treat thrombocytopenia in patients with chronic liver disease (CLD) or chronic immune thrombocytopenia (ITP).",
                  legalStatus: "PrescriptionOnly",
                  drugClass: {
                    name: "Thrombopoietin receptor agonist",
                  },
                  administrationRoute: "Oral",
                  dosageForm: ["Tablet", "Sprinkle oral granules"],
                  warning:
                    "May cause thromboembolic events and increase risk of blood clots.",
                },
                about: {
                  "@type": "MedicalCondition",
                  name: "chronic immune thrombocytopenia",
                  alternateName: "ITP",
                },
                lastReviewed: "2025-09-01",
                reviewedBy: {
                  "@type": "Organization",
                  name: "Sobi",
                  url: "https://www.sobi.com/usa/",
                },
              },
            ],
          }),
        ]}
      >
        <title>
          Chronic Immune Thrombocytopenia (ITP) Treatment | DOPTELET®
          (avatrombopag)
        </title>
        <meta
          name="description"
          content="Discover DOPTELET® (avatrombopag), a medication used to increase low blood platelet counts in adults with chronic immune thrombocytopenia (ITP)."
        />
        <link rel="canonical" href="https://doptelet.com/home/" />
      </Helmet>
      <ItpNavigation />
      <div className="inner-content-wrap itpHome">
        <NowApprovedNotification />
        <ITPHeroHeader
          extraClass="large-hero inner-bg home-hero"
          textSitsOnImageForMobile
          markup={
            <>
              <h1>
                GET THE CHRONIC ITP
                <br /> LIFT THAT LASTS
              </h1>
              <p>
                Doptelet is an oral chronic ITP medication that helps increase
                platelet counts to 50,000 per microliter or more, in as few as 8
                days, and can keep them lifted for an average of 12 total weeks.
              </p>
              <p className="mb-0 text-center">
                <Link
                  className="btn btn-primary btn-arrow"
                  to="/what-is-doptelet/"
                >
                  <span>LEARN MORE ABOUT DOPTELET</span>
                </Link>
              </p>
            </>
          }
          maxWidth="525"
          bgImage={["PlateletCarnival_D.png", "PlateletCarnival_D.png"]}
          bgImageMobile={["home-hero.jpg", "home-hero@2x.jpg"]}
        />

        <section className="section-padding home-cards">
          <Container>
            <Row>
              <Col lg={{ span: 12 }}>
                <h2 className="mb-40">Which of these sounds most like you?</h2>
              </Col>
            </Row>
            <Row>
              <Col lg={4} md={12} className="mb-4 mb-lg-0">
                <div className="d-flex flex-column h-100">
                  <div className="d-flex flex-row align-items-center">
                    <picture>
                      <source
                        srcSet="/images/Platey_Sage_Waving.png 1x, /images/Platey_Sage_Waving@2x.png 2x"
                        type="image/png"
                      />
                      <img
                        src="/images/Platey_Sage_Waving.png"
                        alt="Doptelet Platelet Character Waving"
                      />
                    </picture>
                    <h3 className="ml-3 mb-0">
                      <strong>
                        I’m just learning
                        <br className="d-none d-md-block" /> about chronic ITP
                      </strong>
                    </h3>
                  </div>
                  <p className="flex-grow-1">
                    Read all about ITP to learn how it affects the platelets in
                    your blood and how it is diagnosed and treated.
                  </p>
                  <Link
                    to="/what-is-itp/"
                    className="btn btn-primary btn-arrow"
                  >
                    <span>WHAT IS ITP?</span>
                  </Link>
                </div>
              </Col>
              <Col lg={4} md={12} className="mb-4 mb-lg-0">
                <div className="d-flex flex-column h-100">
                  <div className="d-flex flex-row align-items-center">
                    <picture>
                      <source
                        srcSet="/images/Platey_Other_Waving.png 1x, /images/Platey_Other_Waving@2x.png 2x"
                        type="image/png"
                      />
                      <img
                        src="/images/Platey_Other_Waving.png"
                        alt="Doptelet Platelet Character Waving"
                      />
                    </picture>
                    <h3 className="ml-3 mb-0">
                      <strong>
                        I’m looking to
                        <br className="d-none d-md-block" /> manage my ITP
                      </strong>
                    </h3>
                  </div>
                  <p className="flex-grow-1">
                    Is chronic ITP affecting your day-to-day life? View
                    information and guidance for managing low platelet counts.
                  </p>
                  <Link
                    to="/setting-goals/"
                    className="btn btn-primary btn-arrow"
                  >
                    <span>SETTING GOALS</span>
                  </Link>
                </div>
              </Col>
              <Col lg={4} md={12}>
                <div className="d-flex flex-column h-100">
                  <div className="d-flex flex-row align-items-center">
                    <picture>
                      <source
                        srcSet="/images/Platey_Tossing_Kid.png 1x, /images/Platey_Tossing_Kid@2x.png 2x"
                        type="image/png"
                      />
                      <img
                        src="/images/Platey_Tossing_Kid.png"
                        alt="Doptelet Platelet Character Wearing a Hat"
                      />
                    </picture>
                    <h3 className="ml-3 mb-0">
                      <strong>
                        I’m already on
                        <br className="d-none d-md-block" /> Doptelet
                      </strong>
                    </h3>
                  </div>
                  <p className="flex-grow-1">
                    See if you’re eligible for any financial assistance
                    programs.
                  </p>
                  <Link
                    to="/support-and-resources/financial-assistance/"
                    className="btn btn-primary btn-arrow"
                  >
                    <span>FINANCIAL ASSISTANCE INFO</span>
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <LookingForSupport addClass="section-padding pt-0" />

        <ISIITPinline />
      </div>
      <FooterITP />
      <ISIwrapper>
        <ISIITP />
      </ISIwrapper>
      <BackToTopBtn />
    </div>
  );
}

export default ITPHome;
